///
/// EarthBrowser
///
/// Air Quality Grid Service
///
/// Downloads and samples the CAMS air quality EBWX grid (published by
/// weather-services) and computes an indicative AQI for any lat/lon.
/// Follows the same fetch/refresh pattern as WeatherGridService.
///
/// Created by Matt Giger on September 15, 2026
/// Copyright (c) 2026 Geodata Labs
///

import { Constants } from "@/constants"
import { datasetUrl } from "@/utils/datasetUrl"
import { decodeEBWXHeader, decodeEBWXAtPoint, type EBWXHeader } from "../weather-grid/EBWXDecoder"
import { computeAirQuality, type AirQualityInfo, type PollutantConcentrations } from "./AirQualityCompute"

const AIR_QUALITY_GRID_URL = `${Constants.baseStorageUrl}/datasets/air_quality/air_quality_current.bin.gz`
const REFRESH_INTERVAL_MS = 30 * 60 * 1000

/** Dev override for the air quality grid URL (serve a locally built grid) */
function airQualityGridUrl(): string {
	try {
		const override = globalThis.localStorage?.getItem("earthbrowser_air_quality_grid_url")
		if (override) return override
	} catch { }
	return AIR_QUALITY_GRID_URL
}

const POLLUTANT_KEYS: Array<keyof PollutantConcentrations> = [
	"pm2p5_ugm3", "pm10_ugm3", "o3_ppb", "no2_ppb", "so2_ppb", "co_ppb",
]

export class AirQualityGridService {
	private grid: { buffer: ArrayBuffer, header: EBWXHeader } | null = null
	private gridTimestamp = 0
	private loading: Promise<void> | null = null

	async ensureGrid(): Promise<void> {
		const now = Date.now()
		// Fresh by timestamp even after a failed fetch, so a missing grid
		// (dataset not published yet) doesn't hammer the CDN on every query
		if ((now - this.gridTimestamp) < REFRESH_INTERVAL_MS) {
			return
		}

		if (this.loading) {
			await this.loading
			return
		}

		this.loading = this._loadGrid()
		try {
			await this.loading
		} finally {
			this.loading = null
		}
	}

	private async _loadGrid(): Promise<void> {
		try {
			const response = await fetch(datasetUrl(airQualityGridUrl()))
			if (!response.ok) {
				console.warn(`[AirQuality] Failed to fetch grid: ${response.status}`)
				this.gridTimestamp = Date.now()
				return
			}

			// B2 serves raw gzip bytes, but some servers (e.g. the dev server)
			// send Content-Encoding: gzip and the browser has already
			// decompressed the body - detect by the gzip magic bytes
			const raw = await response.arrayBuffer()
			const bytes = new Uint8Array(raw)
			const isGzip = bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b
			const decompressed = isGzip ? await decompressGzip(raw) : raw
			const header = decodeEBWXHeader(decompressed)

			this.grid = { buffer: decompressed, header }
			this.gridTimestamp = Date.now()

			console.log(`[AirQuality] Grid loaded: ${header.nTimesteps} timesteps, ${header.nx}x${header.ny} grid`)
		} catch (err) {
			console.error('[AirQuality] Error loading grid:', err)
			this.gridTimestamp = Date.now()
		}
	}

	/**
	 * Sample pollutant concentrations at a point, interpolate them to the
	 * current time (clamped to the published window), and compute the AQI.
	 */
	async getCurrentAirQuality(lat: number, lon: number): Promise<AirQualityInfo | null> {
		await this.ensureGrid()
		if (!this.grid) return null

		const timesteps = decodeEBWXAtPoint(this.grid.buffer, this.grid.header, lat, lon)
		if (timesteps.length === 0) return null

		const nowSec = Date.now() / 1000
		const first = timesteps[0]
		const last = timesteps[timesteps.length - 1]
		const timeSec = Math.min(Math.max(nowSec, first.validTime), last.validTime)

		const pollutants: PollutantConcentrations = {
			pm2p5_ugm3: null, pm10_ugm3: null, o3_ppb: null, no2_ppb: null, so2_ppb: null, co_ppb: null,
		}

		for (const key of POLLUTANT_KEYS) {
			pollutants[key] = sampleValueAt(timesteps, key, timeSec)
		}

		return computeAirQuality(pollutants, timeSec)
	}

	clearCache(): void {
		this.grid = null
		this.gridTimestamp = 0
	}
}

interface TimestepValues {
	validTime: number
	values: Record<string, number | null>
}

/** Linear time interpolation of one variable, clamped to the available range */
function sampleValueAt(timesteps: TimestepValues[], key: string, timeSec: number): number | null {
	const usable = timesteps.filter(ts => (ts.values[key] ?? null) !== null)
	if (usable.length === 0) return null

	const first = usable[0]
	const last = usable[usable.length - 1]
	if (timeSec <= first.validTime) return first.values[key]!
	if (timeSec >= last.validTime) return last.values[key]!

	for (let i = 0; i < usable.length - 1; i++) {
		const a = usable[i]
		const b = usable[i + 1]
		if (timeSec >= a.validTime && timeSec <= b.validTime) {
			const t = (timeSec - a.validTime) / (b.validTime - a.validTime)
			return a.values[key]! + (b.values[key]! - a.values[key]!) * t
		}
	}
	return null
}

async function decompressGzip(compressed: ArrayBuffer): Promise<ArrayBuffer> {
	const ds = new DecompressionStream('gzip')
	const writer = ds.writable.getWriter()

	// don't await here: writes can block on backpressure until the reader drains
	const writeDone = (async () => {
		await writer.write(new Uint8Array(compressed))
		await writer.close()
	})()

	const reader = ds.readable.getReader()
	const chunks: Uint8Array[] = []
	let totalLength = 0

	while (true) {
		const { done, value } = await reader.read()
		if (done) break
		chunks.push(value)
		totalLength += value.length
	}

	await writeDone

	const result = new Uint8Array(totalLength)
	let offset = 0
	for (const chunk of chunks) {
		result.set(chunk, offset)
		offset += chunk.length
	}

	return result.buffer
}

let _globalInstance: AirQualityGridService | null = null

export function getAirQualityGridService(): AirQualityGridService {
	if (!_globalInstance) {
		_globalInstance = new AirQualityGridService()
	}
	return _globalInstance
}
